import type { SimEntity, TeamState, WorldState } from "./types";
import { FfaEvent } from "../sim-events";
import { C, clampToPlay, emit } from "./shared";

function maxMarchSec(): number {
  const c = (
    C as { combat?: { maxMarchSec?: number; moveGraceSec?: number } }
  ).combat;
  return Number(c?.maxMarchSec) || Number(c?.moveGraceSec) || 8;
}

export function postArriveSleepSec(): number {
  const v = Number(
    (C as { combat?: { postArriveSleepSec?: number } }).combat
      ?.postArriveSleepSec,
  );
  return Number.isFinite(v) ? v : 0.3;
}

function clearTeamMarching(world: WorldState, teamId: number, sleep: number): void {
  for (const e of world.entities) {
    if (e.teamId !== teamId || !e.marching) continue;
    e.marching = false;
    e.combatSleepUntil = world.time + sleep;
  }
}

export function setTeamGoal(
  world: WorldState,
  teamId: number,
  x: number,
  y: number,
): void {
  const team = world.teams[teamId];
  if (!team?.alive) return;
  const { x: gx, y: gy } = clampToPlay(world, x, y);
  const moved = Math.hypot(gx - team.goalX, gy - team.goalY) > 8;
  team.goalX = gx;
  team.goalY = gy;
  if (moved && team.isPlayer) {
    team.moveOrderActive = true;
    // Safety ceiling only — march ends early when master arrives.
    team.moveGraceUntil = world.time + maxMarchSec();
    for (const e of world.entities) {
      if (e.teamId !== teamId) continue;
      // Stationary artillery never marches — otherwise marching sticks forever.
      if (e.role === "weapon") continue;
      e.arrived = false;
      e.marching = true;
      e.combatSleepUntil = 0;
    }
    emit(world, {
      kind: FfaEvent.GoalSet,
      time: world.time,
      teamId,
      x: gx,
      y: gy,
    });
  }
}

/** Call after setDesiredToward when a unit may have just arrived. */
export function onUnitMaybeArrived(world: WorldState, e: SimEntity): void {
  if (!e.arrived || !e.marching) return;
  e.marching = false;
  e.combatSleepUntil = world.time + postArriveSleepSec();

  const team = world.teams[e.teamId];
  if (!team) return;
  if (e.role === "master" && team.moveOrderActive) {
    team.moveOrderActive = false;
    team.moveGraceUntil = world.time;
    // Flush leftover marchers (e.g. never-arriving edge cases).
    clearTeamMarching(world, team.id, postArriveSleepSec());
  }
}

/** Expire overdue move orders; remaining marchers get brief post-arrive sleep. */
export function tickMoveOrders(world: WorldState): void {
  const sleep = postArriveSleepSec();
  for (const team of world.teams) {
    if (!team.moveOrderActive) continue;
    if (world.time < team.moveGraceUntil) continue;
    team.moveOrderActive = false;
    clearTeamMarching(world, team.id, sleep);
  }
}

export function shouldDeferCombat(
  world: WorldState,
  _team: TeamState,
  e: SimEntity,
): boolean {
  // Turrets never participate in forced march — always free to fire in range.
  if (e.role === "weapon") return false;
  if (e.marching) return true;
  return world.time < e.combatSleepUntil;
}
